/**
 * Inline form editor v2 hooks.
 *
 * InlineDescription — turns the campaign description into the shared CKEditor
 *   inline editor. It keeps changes local and pushes `save_description` only
 *   when the user clicks Save. The element should carry `phx-update="ignore"`
 *   so LiveView patches never clobber the caret.
 *
 * SortableQuestions — drag-to-reorder the question previews. Pushes
 *   `reorder_questions` with the new ordered list of question ids.
 */

import Sortable from "sortablejs";
import { createInlineEditor } from "../rich-text-editor.js";

export const InlineDescription = {
  mounted() {
    this.dirty = false;
    this.saveButton = document.querySelector("[data-fe2-save-description]");
    this.savedHtml = this.el.innerHTML;
    this.onSaveClick = (e) => this.save(e);
    if (this.saveButton) {
      this.saveButton.addEventListener("click", this.onSaveClick);
      this.syncSaveButton();
    }

    createInlineEditor(this.el, this.savedHtml)
      .then((editor) => {
        this.editor = editor;
        this.editor.model.document.on("change:data", () => this.onInput());
      })
      .catch((error) => console.error(error));
  },

  destroyed() {
    if (this.editor) {
      this.editor.destroy().catch((error) => console.error(error));
      this.editor = null;
    }
    if (this.saveButton && this.onSaveClick) {
      this.saveButton.removeEventListener("click", this.onSaveClick);
    }
  },

  onInput() {
    const currentHtml = this.editor ? this.editor.getData() : this.el.innerHTML;
    this.dirty = currentHtml !== this.savedHtml;
    this.syncSaveButton();
  },

  save(e) {
    if (!this.dirty) return;
    e.preventDefault();
    e.stopImmediatePropagation();
    const html = this.editor ? this.editor.getData() : this.el.innerHTML;
    this.pushEvent("save_description", { html }, () => {
      this.savedHtml = html;
      this.dirty = false;
      this.syncSaveButton();
    });
  },

  syncSaveButton() {
    if (!this.saveButton) return;
    this.saveButton.disabled = !this.dirty;
    this.saveButton.classList.toggle("opacity-45", !this.dirty);
    this.saveButton.classList.toggle("cursor-not-allowed", !this.dirty);
  },
};

export const SortableQuestions = {
  mounted() {
    this.init();
  },

  updated() {
    this.init();
  },

  destroyed() {
    if (this.sortable) this.sortable.destroy();
  },

  init() {
    if (this.sortable) {
      this.sortable.destroy();
      this.sortable = null;
    }
    if (this.el.dataset.disabled === "true") return;

    const hook = this;
    this.sortable = new Sortable(this.el, {
      animation: 150,
      handle: ".fe2-grab",
      draggable: "[data-qid]",
      ghostClass: "fe2-dragging",
      onEnd: () => {
        const ids = Array.from(
          hook.el.querySelectorAll(":scope > [data-qid]"),
        ).map((el) => el.dataset.qid);
        hook.pushEvent("reorder_questions", { ids });
      },
    });
  },
};
